import { traducirError, useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, Ventana } from '@tallerya/ui';
import { useState } from 'react';
import { useCambiosItem, type FichaItem } from './datos';

// «Eliminar» del menú de la fila, con la forma de la ventana de desactivar del
// Figma. Solo se elimina lo que no aparece en ninguna orden: lo dice la ficha,
// con la misma cuenta que usa la API para negarse (`CountWithCatalogItemAsync`).
//
// Por eso la ventana tiene dos caras:
//   - sin órdenes: «¿Eliminar este servicio?», y se elimina;
//   - con órdenes: «No se puede eliminar», y ofrece desactivarlo, que es lo que
//     de verdad quiere quien lo deja de vender.
//
// El 409 de la API sigue haciendo falta: alguien pudo cargar el ítem en una
// orden, ya confirmada, mientras la ventana estaba abierta.

interface Props {
  ficha: FichaItem | null;
  onCerrar: () => void;
  onEliminado: () => void;
  /** Pasa a la ventana de desactivar, con el mismo ítem. */
  onDesactivar: () => void;
}

export function EliminarItem({ ficha, onCerrar, onEliminado, onDesactivar }: Props) {
  const { t } = useTraduccion();
  const cambios = useCambiosItem();
  const [enCurso, setEnCurso] = useState(false);
  const [rechazo, setRechazo] = useState<string | null>(null);

  if (!ficha) return null;

  const servicio = ficha.kind === 'service';
  const ordenes = Number(ficha.orderCount);

  async function confirmar() {
    if (!ficha) return;
    setEnCurso(true);
    setRechazo(null);
    try {
      const problema = await cambios.eliminar(String(ficha.id));
      if (problema) {
        setRechazo(problema.code ? traducirError(problema.code, t) : t('admin.catalogo.error.reintentar'));
        return;
      }
      onEliminado();
    } catch {
      setRechazo(t('admin.catalogo.error.reintentar'));
    } finally {
      setEnCurso(false);
    }
  }

  const cancelar = (
    <Boton variante="secundario" disabled={enCurso} onClick={onCerrar}>
      {t('admin.catalogo.eliminar.cancelar')}
    </Boton>
  );

  if (ordenes > 0) {
    return (
      <Ventana
        abierta
        titulo={
          servicio ? t('admin.catalogo.eliminar.noSePuedeServicio') : t('admin.catalogo.eliminar.noSePuedeProducto')
        }
        descripcion={t('admin.catalogo.eliminar.noSePuedeTexto', { nombre: ficha.name })}
        acciones={
          ficha.isActive ? (
            <>
              {cancelar}
              <Boton variante="peligro" onClick={onDesactivar}>
                {t('admin.catalogo.eliminar.desactivar')}
              </Boton>
            </>
          ) : (
            <Boton onClick={onCerrar}>{t('admin.catalogo.confirmar.cerrar')}</Boton>
          )
        }
      >
        <div className="confirmar__caja">
          <strong>
            {ordenes === 1
              ? t('admin.catalogo.desactivar.unaOrden')
              : t('admin.catalogo.desactivar.ordenes', { n: ordenes })}
          </strong>
          <p>
            {ficha.isActive
              ? t('admin.catalogo.eliminar.desactivarEnSuLugar')
              : t('admin.catalogo.eliminar.yaInactivo')}
          </p>
        </div>
      </Ventana>
    );
  }

  return (
    <Ventana
      abierta
      titulo={servicio ? t('admin.catalogo.eliminar.tituloServicio') : t('admin.catalogo.eliminar.tituloProducto')}
      descripcion={t('admin.catalogo.eliminar.texto', { nombre: ficha.name })}
      acciones={
        <>
          {cancelar}
          <Boton variante="peligro" disabled={enCurso} aria-busy={enCurso} onClick={() => void confirmar()}>
            {t('admin.catalogo.eliminar.confirmar')}
          </Boton>
        </>
      }
    >
      <div className="confirmar__caja">
        <strong>{t('admin.catalogo.eliminar.sinOrdenes')}</strong>
        <p>{t('admin.catalogo.eliminar.sinOrdenesTexto')}</p>
      </div>
      <p className="confirmar__nota">{t('admin.catalogo.eliminar.irreversible')}</p>
      {rechazo && <AvisoError aviso={{ titulo: t('admin.catalogo.error.titulo'), texto: rechazo }} />}
    </Ventana>
  );
}
